import React, { useState, useEffect } from "react";
import translator from "@/translator";
import { Button, message, Spin } from "antd";
import Helper from "@/helper";

const t = translator("agent_office.customers.payment_modal.");

const AgentCustomerPaymentModal = ({ subscription_plans, available_periods, commission, translations }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [submitUrl, setSubmitUrl] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(available_periods?.[0]?.value || "");
  const [selectedPlan, setSelectedPlan] = useState(subscription_plans?.[0]?.name || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const csrfToken = Helper.getCSRFToken();

  useEffect(() => {
    const handleOpenModal = (event) => {
      const { customerId, submitUrl } = event.detail;

      setCustomerId(customerId);
      setSubmitUrl(submitUrl);
      setIsOpen(true);

      setSelectedPeriod(available_periods?.[2]?.value || "");
      setSelectedPlan(subscription_plans?.[1]?.name || "");
      setIsSubmitting(false);
    };

    window.addEventListener("openPaymentModal", handleOpenModal);

    return () => {
      window.removeEventListener("openPaymentModal", handleOpenModal);
    };
  }, []);

  const onClose = () => {
    setIsOpen(false);
    setCustomerId(null);
    setSubmitUrl(null);
  };

  const formatPrice = (val) => {
    return val.toLocaleString("ru-RU") + " ₽";
  };

  const calculateSummary = () => {
    if (!subscription_plans || !available_periods) return null;

    const tariff = subscription_plans.find((p) => p.name === selectedPlan);
    const period = available_periods.find((p) => p.value === selectedPeriod);

    if (!tariff || !period) return null;

    const agentcommission = commission * 100;
    const basePrice = (tariff.price || 0) * parseInt(selectedPeriod);
    const clientPrice = basePrice * (1 - (period.discount / 100));
    const totalPrice = clientPrice * (1 - commission);
    const discount = clientPrice * commission;

    return {
      tariff,
      period,
      basePrice,
      discount,
      clientPrice,
      totalPrice,
      agentcommission
    };
  };

  const summary = calculateSummary();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("customer[period]", selectedPeriod);
    formData.append("customer[subscription_plan_name]", selectedPlan);

    try {
      const response = await fetch(submitUrl, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = await response.json();

      if (response.ok) {
        message.success(t("success_message"));
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 3000);
      } else {
        console.error("Payment failed");
        setIsSubmitting(false);
        message.error(data.error);
      }
    } catch (error) {
      console.error("Payment error:", error);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return <div style={{ display: "none" }}></div>;
  }

  return (
    <div className="agents_customer_payment_modal">
      <div className="ant-modal-content modal-content">
        <form onSubmit={handleSubmit}>
          <div className="ant-modal-header">
            <div className="ant-modal-title">{t("title")}</div>
            <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="ant-modal-body" style={{ padding: "24px" }}>
            <div>
              <h3 className="tariff-group__title">{t("channels_title")}</h3>
              <div className="channel-info">
                <p className="channel-info__title">{t("channels_label", { count: 1 })}</p>
              </div>
            </div>

            <div className="subscription-group">
              <h3 className="tariff-group__title">{t("select_period")}</h3>
              <div className="subscription-group__container">
                {(available_periods || []).map((period, index) => {
                  return (
                    <label
                      key={period.value}
                      className={`ant-radio-wrapper ${selectedPeriod === period.value ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedPeriod(period.value);
                      }}
                    >
                      <div className="subscription-item">
                        <input
                          type="radio"
                          name="period"
                          value={period.value}
                          checked={selectedPeriod === period.value}
                          onChange={(e) => {
                            setSelectedPeriod(e.target.value);
                          }}
                        />
                        <strong>{period.name}</strong>
                      </div>
                      {period.discount > 0 && (
                        <span className="subscription-item__sale">
                          {t("discount", { percent: period.discount })}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="tariff-group">
              <h3 className="tariff-group__title">{t("select_tariff")}</h3>
              <div className="ant-radio-group tariff-group__container">
                {(subscription_plans || []).map((plan) => (
                  <label
                    key={plan.name}
                    className={`ant-radio-wrapper ${selectedPlan === plan.name ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedPlan(plan.name);
                    }}
                  >
                    <div className="tariff-item">
                      <div className="tariff-item__type">
                        <input
                          type="radio"
                          name="subscription_plan_name"
                          value={plan.name}
                          checked={selectedPlan === plan.name}
                          onChange={(e) => {
                            setSelectedPlan(e.target.value);
                          }}
                        />
                        <strong>{plan.display_name}</strong>
                      </div>
                      <div>
                        <p className="tariff-item__price">
                          {t("price_per_month", {
                            price: (plan.price || 0).toLocaleString("ru-RU", {
                              minimumFractionDigits: 0,
                            }),
                          })}
                        </p>
                      </div>
                    </div>
                    {plan.description && (
                      <span className="tariff-item__description">{plan.description}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {summary && (
              <div className="payment-summary">
                <div className="payment-summary__item payment-summary__header">
                  <div className="payment-summary__title">{translations.summary_title}</div>
                  <div className="payment-summary__title-result">
                    {summary.tariff.display_name}, {t("channels_label", { count: 1 })},{" "}
                    {summary.period.name}
                  </div>
                </div>
                <div className="payment-summary__item payment-summary__price">
                  <div className="payment-summary__text">{translations.base_price_label}</div>
                  <div className="payment-summary__title-result">
                    {formatPrice(summary.basePrice)}
                  </div>
                </div>
                <div className="payment-summary__item payment-summary__details">
                  <div className="payment-summary__text">{translations.client_price_label}</div>
                  <div className="payment-summary__title-result">
                    {formatPrice(summary.clientPrice)}
                  </div>
                </div>
                {summary.discount > 0 && (
                  <div className="payment-summary__item payment-summary__sale">
                    <div className="payment-summary__text">
                      {translations.savings_label.replace(
                        "${percent}",
                        Math.round(summary.agentcommission),
                      )}
                    </div>
                    <div className="payment-summary__title-result payment-summary__discount-result">
                      {formatPrice(summary.discount)}
                    </div>
                  </div>
                )}
                <div className="payment-summary__item payment-summary__total">
                  <div className="payment-summary__title-pay">
                    <b>{translations.total_to_pay_label}</b>
                  </div>
                  <div className="payment-summary__title-pay">
                    {formatPrice(summary.totalPrice)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="ant-btn ant-btn-default modal-back-btn"
              onClick={onClose}
            >
              {t("back")}
            </button>
            <Button
              type="primary"
              htmlType="submit"
              className="modal-submit-btn"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t("pay")}
            </Button>
          </div>
        </form>
        {isSubmitting && <Spin size="large" className="modal-loader" />}
      </div>
    </div>
  );
};

export default AgentCustomerPaymentModal;
